// src/views/UserViews/insights/components/UtilizationProgressCard.tsx
//
// Sibling to MetricCard, purpose-built for the "Reported this period" card: a
// numerator/denominator progress bar plus an independent 14-day stale-device caption below
// it. A two-sided "X of Y" bar has no equivalent in MetricCard's value/caption contract (see
// that file's header) - this borrows MetricCard's label-row/Card sizing and hover-reveal
// mechanism verbatim (duplicated constants, not shared, for visual parity - see MetricCard's
// own comments for the reveal mechanism's full reasoning) so the two read as natural siblings.
//
// Rule 1, for a ratio: `numerator`/`denominator` are independently nullable, and
// `denominator === 0` is also "not enough data" (avoids a divide-by-zero NaN/Infinity bar
// value). A genuine `numerator === 0` against a real denominator (e.g. "0 of 21") IS valid
// data and renders as a real, unfilled bar.
//
// The stale-device reveal line is independent of the numerator/denominator bar state - a bar
// reading "not enough data" can still reveal a real stale count.
//
// `revealOnHover` (mirrors MetricCard's own prop - see that file's header): this card's one
// caller needs the pre-restyle always-visible-caption + separate-icon-tooltip behavior back
// instead of the whole-card reveal; defaults to `true`.

import { Box, Card, CardContent, LinearProgress, Skeleton, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import { NO_TELEMETRY_STALE_THRESHOLD_DAYS } from '@/repository/InsightsDAO';
import {
  INSIGHTS_CARD_BORDER_COLOR,
  INSIGHTS_CARD_BORDER_RADIUS,
  INSIGHTS_CARD_BOX_SHADOW,
} from './cardSurfaceStyles';

type UtilizationProgressCardProps = {
  /** Stable id used to build this card's data-testids, same convention as MetricCard's
   * `testId`. */
  testId: string;
  label: string;
  /** One-line definition normally shown in an info tooltip beside the label - unless this
   * card also has a stale-device count to reveal, in which case it merges into the same
   * whole-card reveal row that uses (see `revealText` below). */
  tooltip?: string;
  /** Numerator - distinct devices that reported in the selected window. `null` means "not
   * enough data" (Rule 1), never a coerced zero. */
  numerator: number | null;
  /** Denominator - the org's all-time reporting-device count. `null` OR `0` both mean "not
   * enough data" (Rule 1) - a zero denominator would otherwise divide-by-zero into a
   * NaN/Infinity bar. */
  denominator: number | null;
  /** Independent 14-day stale-device count, revealed in the whole-card hover/focus row
   * below the bar (see header comment). Not folded into the numerator/denominator null
   * check above. */
  staleDeviceCount: number | null;
  loading?: boolean;
  error?: Error | null;
  /** `true` (default) preserves the existing whole-card hover/focus reveal (see header
   * comment). `false` renders the stale-device line as a permanent caption under the bar
   * and `tooltip` as the label's own icon+`Tooltip`, with none of the reveal machinery. */
  revealOnHover?: boolean;
};

// Matches MetricCard's own constants so the two cards read as visually consistent siblings.
// Border tokens below come from shared `cardSurfaceStyles`, same reason as MetricCard.
const CARD_PADDING = 24;
const CARD_MIN_HEIGHT = 145;
const CARD_ROW_GAP = 10;

// Row 1 - label, matches MetricCard's label row.
const LABEL_FONT_WEIGHT = 700;

// Row 2 - numerator, same weight/size as MetricCard's headline value.
const VALUE_FONT_SIZE = '1.875rem'; // 30px
const VALUE_FONT_WEIGHT = 700;
const VALUE_LINE_HEIGHT = 1.1;

// Row 2's "of Y ..." pairing and null-state caption - matches MetricCard's caption row.
const CAPTION_FONT_SIZE = '0.8125rem'; // 13px
const CAPTION_FONT_WEIGHT = 500;
const CAPTION_LINE_HEIGHT = 1.5;

// Reveal row text, one step larger than the caption row - matches MetricCard's REVEAL_TEXT_*
// constants exactly so both cards' reveal rows stay visually identical.
const REVEAL_TEXT_FONT_SIZE = '0.875rem'; // 14px
const REVEAL_TEXT_FONT_WEIGHT = 500;
const REVEAL_TEXT_LINE_HEIGHT = 1.5;

// The skeleton stands in for the value + bar rows together, so a loading card doesn't
// collapse shorter than a populated one.
const SKELETON_VALUE_WIDTH = 96;
const SKELETON_VALUE_HEIGHT = 33;

// Matches the established `LinearProgress` convention in VersionDistributionChart.tsx
// (height 8, fully rounded pill, light-grey track) rather than inventing a new bar style.
const BAR_HEIGHT = 8;
const BAR_BORDER_RADIUS = 999;

const EM_DASH = '\u2014';
const PERCENT_MULTIPLIER = 100;
const MIN_PERCENT = 0;
const MAX_PERCENT = 100;

// Hover/focus reveal row - identical mechanism/sizing to MetricCard's own, duplicated (not
// shared) for visual parity between sibling cards, not code reuse.
const REVEAL_ROW_CLASS = 'utilization-card-reveal-row';
// No explicit reserved height - as an in-flow flex child at opacity:0, the row already
// occupies its content height at rest (mirrors MetricCard's identical fix).
const REVEAL_ROW_TRANSITION = 'opacity 0.2s ease';

const FOCUS_OUTLINE_WIDTH = 2; // px
const FOCUS_OUTLINE_OFFSET = 2; // px

// Shadow-only hover/focus affordance - the `borderColor` swap this used to pair with is
// gone (that token read as a black outline). Mirrors MetricCard's identical fix.
const ACTIVE_BOX_SHADOW = 2; // MUI elevation index, resolved via theme.shadows[2]

// Matches MetricCard's own LABEL_SX exactly.
const LABEL_SX = {
  fontWeight: LABEL_FONT_WEIGHT,
};

const VALUE_SX = {
  fontSize: VALUE_FONT_SIZE,
  fontWeight: VALUE_FONT_WEIGHT,
  lineHeight: VALUE_LINE_HEIGHT,
  color: 'primary.main',
};

const CAPTION_SX = {
  fontSize: CAPTION_FONT_SIZE,
  fontWeight: CAPTION_FONT_WEIGHT,
  lineHeight: CAPTION_LINE_HEIGHT,
};

// Byte-for-byte identical to MetricCard's own REVEAL_TEXT_SX.
const REVEAL_TEXT_SX = {
  fontSize: REVEAL_TEXT_FONT_SIZE,
  fontWeight: REVEAL_TEXT_FONT_WEIGHT,
  lineHeight: REVEAL_TEXT_LINE_HEIGHT,
};

/** Clamps a computed percent into [0, 100] - defensive; a bar `value` outside that range is
 * invalid MUI input. */
function clampPercent(value: number): number {
  return Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, value));
}

export default function UtilizationProgressCard({
  testId,
  label,
  tooltip,
  numerator,
  denominator,
  staleDeviceCount,
  loading = false,
  error = null,
  revealOnHover = true,
}: UtilizationProgressCardProps) {
  const { t } = useTranslation();

  const notLoadingOrError = !loading && !error;
  // Gated on loading/error so a stale count is never revealed alongside a mid-load/failed
  // card. `days` is interpolated from `NO_TELEMETRY_STALE_THRESHOLD_DAYS` (InsightsDAO) so
  // this copy can't drift from the real threshold.
  const staleLineText = notLoadingOrError && staleDeviceCount !== null
    ? t(StringKeys.INSIGHTS_NO_TELEMETRY_14_DAYS, {
        count: staleDeviceCount,
        days: NO_TELEMETRY_STALE_THRESHOLD_DAYS,
      })
    : undefined;
  // Merges stale count + label tooltip into one whole-card reveal row (mirrors MetricCard's
  // own reveal - see that file's header). `revealOnHover=false` unconditionally suppresses it.
  const revealText = revealOnHover && notLoadingOrError
    ? staleLineText && tooltip
      ? `${staleLineText}. ${tooltip}`
      : staleLineText ?? tooltip
    : undefined;
  const labelTooltipText = revealText ? undefined : tooltip;
  // Opted-out instances get the stale line back as a permanent caption under the bar instead.
  const showPermanentStaleLine = !revealOnHover && Boolean(staleLineText);

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        minHeight: CARD_MIN_HEIGHT,
        borderColor: INSIGHTS_CARD_BORDER_COLOR,
        borderRadius: `${INSIGHTS_CARD_BORDER_RADIUS}px`,
        boxShadow: INSIGHTS_CARD_BOX_SHADOW,
        // Focus-outline safety net only for revealOnHover instances.
        ...(revealOnHover && {
          outline: `${FOCUS_OUTLINE_WIDTH}px solid transparent`,
          outlineOffset: `${FOCUS_OUTLINE_OFFSET}px`,
          transition: 'box-shadow 0.2s ease',
          '&:focus-visible': { outlineColor: 'primary.main' },
        }),
        // borderColor stays untouched in both states - see ACTIVE_BOX_SHADOW's comment.
        ...(revealText && {
          [`&:hover .${REVEAL_ROW_CLASS}`]: { opacity: 1 },
          [`&:focus-within .${REVEAL_ROW_CLASS}`]: { opacity: 1 },
          '&:hover': { boxShadow: ACTIVE_BOX_SHADOW },
          '&:focus-within': { boxShadow: ACTIVE_BOX_SHADOW },
        }),
      }}
      data-testid={testId}
      {...(revealText
        ? { tabIndex: 0, role: 'group', 'aria-label': revealText }
        : {})}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: `${CARD_ROW_GAP}px`,
          p: `${CARD_PADDING}px`,
          '&:last-child': { pb: `${CARD_PADDING}px` },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="subtitle1"
            component="span"
            sx={LABEL_SX}
          >
            {label}
          </Typography>
          {labelTooltipText && (
            <Tooltip title={labelTooltipText} placement="top" arrow>
              <InfoOutlinedIcon
                aria-label={labelTooltipText}
                sx={{ fontSize: '1rem', color: 'text.secondary' }}
              />
            </Tooltip>
          )}
        </Box>

        {loading && (
          <Skeleton
            variant="text"
            width={SKELETON_VALUE_WIDTH}
            height={SKELETON_VALUE_HEIGHT}
            data-testid={`${testId}-loading`}
          />
        )}

        {!loading && error && (
          <Typography variant="body2" color="error" data-testid={`${testId}-error`}>
            {t(StringKeys.INSIGHTS_SECTION_LOAD_ERROR)}
          </Typography>
        )}

        {!loading && !error && (numerator === null || denominator === null || denominator === 0) && (
          <>
            <Typography variant="h4" component="p" data-testid={`${testId}-empty`} sx={VALUE_SX}>
              {EM_DASH}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={CAPTION_SX}>
              {t(StringKeys.INSIGHTS_METRIC_NOT_ENOUGH_DATA)}
            </Typography>
          </>
        )}

        {!loading && !error && numerator !== null && denominator !== null && denominator !== 0 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 0.5 }}>
              <Typography component="span" data-testid={`${testId}-value`} sx={VALUE_SX}>
                {numerator}
              </Typography>
              <Typography component="span" color="text.secondary" sx={CAPTION_SX}>
                {t(StringKeys.INSIGHTS_DEVICE_UTILIZATION_CAPTION, {
                  count: denominator.toLocaleString(),
                })}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={clampPercent((numerator / denominator) * PERCENT_MULTIPLIER)}
              data-testid={`${testId}-bar`}
              sx={{
                mt: 1,
                height: BAR_HEIGHT,
                borderRadius: BAR_BORDER_RADIUS,
                bgcolor: 'grey.100',
                // Reuses the theme's `success.main` green - same token as a positive-tone
                // MetricCard tone dot.
                '& .MuiLinearProgress-bar': { backgroundColor: 'success.main' },
              }}
            />
            {showPermanentStaleLine && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ...CAPTION_SX, display: 'block', mt: 1 }}
                data-testid={`${testId}-stale-caption`}
              >
                {staleLineText}
              </Typography>
            )}
          </Box>
        )}

        {/* Independent of the bar's own branch above - a bar reading "not enough data" can
         * still reveal a real stale count or label definition. */}
        {revealText && (
          <Box
            className={REVEAL_ROW_CLASS}
            data-testid={`${testId}-reveal-row`}
            // Already exposed via the card's own aria-label above, so this stays out of
            // the accessibility tree to avoid double-announcing.
            aria-hidden="true"
            sx={{
              opacity: 0,
              transition: REVEAL_ROW_TRANSITION,
              // Passive reveal - never intercepts pointer events meant for the card.
              pointerEvents: 'none',
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ ...REVEAL_TEXT_SX, display: 'block' }}
            >
              {revealText}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
